import {
  Alert,
  Button,
  Checkbox,
  CloseButton,
  CondensedButton,
  Flex,
  FormFieldGroup,
  Grid,
  Heading,
  IconTrashLine,
  List,
  Modal,
  Popover,
  ScreenReaderContent,
  Spinner,
  Text,
  TextInput,
  View,
} from "@instructure/ui";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CourseGroupService } from "../../services/CourseGroupService";
import { CourseService } from "../../services/CourseService";
import { sort } from "../../utils/utils";
import { Card } from "../Card";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import {
  CourseGroupTypesContext,
  CourseLevelsContext,
  SubjectContext,
} from "../OrganizationalStructure/OrganizationalStructure";
import { MULTI, SINGLE, Selector } from "../Selectors/Selector";
import AddContextPopover from "./AddContextPopover";
import ManageGroupTypesModal from "./ManageGroupTypesModal";

const CourseGroupModal = ({
  open,
  setOpen,
  data,
  setData,
  setTrigger,
  title,
}) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const { subjects } = useContext(SubjectContext);
  const courseLevels = useContext(CourseLevelsContext);
  const [groupTypes, setGroupTypes] = useContext(CourseGroupTypesContext);
  const [manageModalOpen, setManageModalOpen] = useState(false);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [selectedGroupType, setSelectedGroupType] = useState(null);
  const [selectedDeps, setSelectedDeps] = useState([]);
  const [submitLoad, setSubmitLoad] = useState(false);
  const [submitFindLoad, setSubmitFindLoad] = useState(false);
  const [invalidGroupType, setInvalidGroupType] = useState(false);
  const [invalidCourseGroupName, setInvalidCourseGroupName] = useState(false);
  const [openContextPopover, setOpenContextPopover] = useState(false);
  const [noCourses, setNoCourses] = useState(false);
  const courseGroupNameRef = useRef();

  useEffect(() => {
    if (data?.courses && open) {
      setSelectedGroupType(data.group_type);
      setSelectedCourses(data.courses);
    }
  }, [data, groupTypes, open]);

  const handleFindCourses = () => {
    setSubmitFindLoad(true);
    CourseService.getCourses({
      ownerCourseCode: selectedDeps.map((dep) => dep.title),
      courseLevels: selectedLevels,
      approval: "off",
      withOutcomes: false,
    })
      .then((data) => {
        setNoCourses(data.data.length === 0);
        setFilteredCourses(sort(data.data, (course) => course.code));
        setSubmitFindLoad(false);
      })
      .catch(() => {
        showNotification(
          t("notifications.general.fetchError", { obj: t("programs.courses") }),
          ERROR
        );
      });
  };

  useEffect(() => {
    if (selectedDeps.length === 0) {
      setSelectedLevels([]);
      setFilteredCourses([]);
      setNoCourses(false);
    }
  }, [selectedDeps]);

  const handleSubmit = () => {
    if (!selectedGroupType || !courseGroupNameRef.current.value) {
      if (!courseGroupNameRef.current.value) {
        setInvalidCourseGroupName(true);
      }
      if (!selectedGroupType) {
        setInvalidGroupType(true);
      }
    } else if (data) {
      setInvalidGroupType(false);
      setInvalidCourseGroupName(false);
      setSubmitLoad(true);
      CourseGroupService.updateCourseGroup(
        data.id,
        selectedCourses.map((course) => course.id),
        selectedGroupType,
        courseGroupNameRef.current.value
      )
        .then((newCg) => {
          if (setData) {
            setData((prevData) => ({
              ...prevData,
              name: newCg.name,
              group_type: newCg.groupType,
            }));
          }
          setTrigger(true);
          setOpen(false);
          showNotification(
            t("notifications.general.updateSuccess", {
              obj: t("programs.courseGroup"),
            })
          );
        })
        .catch(() => {
          showNotification(
            t("notifications.general.updateError", {
              obj: t("programs.courseGroup"),
            }),
            ERROR
          );
        });
    } else {
      setInvalidGroupType(false);
      CourseGroupService.createCourseGroup(
        selectedCourses.map((course) => course.id),
        selectedGroupType,
        courseGroupNameRef.current.value
      )
        .then(() => {
          setTrigger(true);
          setOpen(false);
          showNotification(
            t("notifications.general.createSuccess", {
              obj: t("programs.courseGroup"),
            })
          );
        })
        .catch(() => {
          showNotification(
            t("notifications.general.createError", {
              obj: t("programs.courseGroup"),
            }),
            ERROR
          );
        });
    }
  };

  const handleClose = () => {
    setSubmitLoad(false);
    setSubmitFindLoad(false);
    setSelectedGroupType(null);
    setSelectedDeps([]);
    setSelectedLevels([]);
    setFilteredCourses([]);
    setSelectedCourses([]);
    setInvalidCourseGroupName(false);
    setInvalidGroupType(false);
  };
  const handleOpenManageModal = () => {
    setManageModalOpen(true);
  };

  const removeCourse = (courseToRemove) => {
    const newSelectedCourses = selectedCourses.filter(
      (course) => course.id !== courseToRemove
    );
    setSelectedCourses(newSelectedCourses);
  };

  const handleSelectedGroupType = (groupType) => {
    setInvalidGroupType(false);
    setSelectedGroupType(groupType);
  };

  const handleSelectedAcSub = (acSubs) => {
    const selectedSubjects = [];
    acSubs.forEach((sub) => {
      selectedSubjects.push(subjects.find((subject) => subject.id === sub));
    });
    setSelectedDeps(selectedSubjects);
  };

  const handleSelectedLevel = (levels) => {
    if (selectedDeps.length !== 0) {
      const selectedLevels = [];
      levels.forEach((level) => {
        const findingLevel = courseLevels.find((l) => l.id === level);
        if (findingLevel) {
          selectedLevels.push(findingLevel.id);
        }
      });
      setSelectedLevels(selectedLevels);
    }
  };

  const updateSelectedCourses = (courseId) => {
    const index = selectedCourses.findIndex((course) => course.id === courseId);
    if (index === -1) {
      const course = filteredCourses.find((c) => c.id === courseId);
      setSelectedCourses((prevCourses) => [...prevCourses, course]);
    } else {
      setSelectedCourses((prevCourses) => [
        ...prevCourses.slice(0, index),
        ...prevCourses.slice(index + 1),
      ]);
    }
  };

  return (
    <>
      <Modal open={open} size="large" label={title} onExited={handleClose}>
        <Modal.Header>
          <Grid hAlign="space-between">
            <Grid.Row>
              <Grid.Col>
                <Heading level="h2" as="h1">
                  {title}
                </Heading>
              </Grid.Col>
              <Grid.Col width="auto">
                <CloseButton
                  size="small"
                  screenReaderLabel={t("global.close")}
                  onClick={() => setOpen(false)}
                />
              </Grid.Col>
            </Grid.Row>
          </Grid>
        </Modal.Header>
        <Modal.Body>
          <Flex alignItems="start" padding="small" gap="medium">
            <Flex.Item size="50%">
              <TextInput
                size="small"
                interaction="enabled"
                id="course-group-name"
                renderLabel={
                  <Text size="small">{`${t("programs.groupName")}*`}</Text>
                }
                ref={courseGroupNameRef}
                defaultValue={data?.name}
                messages={
                  invalidCourseGroupName
                    ? [
                        {
                          text: (
                            <p
                              style={{
                                fontSize: "0.75rem",
                                marginTop: "-10px",
                              }}
                            >
                              {t("programs.groupNameRequired")}
                            </p>
                          ),
                          type: "error",
                        },
                      ]
                    : []
                }
              />
            </Flex.Item>
            <Flex.Item shouldGrow>
              <Selector
                type={SINGLE}
                size="small"
                id="group-type-selector"
                options={groupTypes}
                selectHandler={handleSelectedGroupType}
                selectedItemId={selectedGroupType}
                messages={
                  invalidGroupType
                    ? [
                        {
                          text: (
                            <p
                              style={{
                                fontSize: "0.75rem",
                                marginTop: "-10px",
                              }}
                            >
                              {t("programs.groupTypeRequired")}
                            </p>
                          ),
                          type: "error",
                        },
                      ]
                    : []
                }
                label={
                  <Flex justifyItems="space-between" alignItems="end">
                    <Flex.Item>
                      <Text size="small">{`${t("programs.groupType")}*`}</Text>
                    </Flex.Item>
                    <Flex.Item>
                      <CondensedButton
                        margin="0 0 0 small"
                        id="add-group-type-button"
                        color="primary"
                        onClick={handleOpenManageModal}
                      >
                        <Text size="x-small">
                          {t("programs.manageGroupTypes")}
                        </Text>
                      </CondensedButton>
                    </Flex.Item>
                  </Flex>
                }
              />
            </Flex.Item>
          </Flex>
          <Flex alignItems="end" padding="small" gap="medium">
            <Flex.Item size="50%">
              <Selector
                type={MULTI}
                size="small"
                id="ac-sub-selector"
                options={subjects}
                selectHandler={handleSelectedAcSub}
                label={<Text size="small">{t("global.departments")}</Text>}
              />
            </Flex.Item>
            <Flex.Item size="33%">
              <Selector
                type={MULTI}
                size="small"
                id="course-level-selector"
                options={courseLevels}
                selectHandler={handleSelectedLevel}
                label={<Text size="small">{t("programs.courseLevels")}</Text>}
                interaction={selectedDeps.length > 0}
              />
            </Flex.Item>
            <Flex.Item shouldGrow textAlign="end">
              <Button
                size="small"
                display="block"
                color="primary"
                onClick={handleFindCourses}
                interaction={
                  selectedDeps.length === 0 || submitFindLoad
                    ? "disabled"
                    : "enabled"
                }
              >
                {submitFindLoad ? (
                  <Spinner
                    renderTitle={t("programs.findingCourses")}
                    size="x-small"
                  />
                ) : (
                  <Text size="small">{t("programs.findCourses")}</Text>
                )}
              </Button>
            </Flex.Item>
          </Flex>
          <Flex alignItems="start" wrap="wrap">
            <Flex.Item size="50%" padding="small">
              <View as="div">
                <Flex
                  justifyItems="space-between"
                  alignItems="center"
                  margin="0 0 small 0"
                >
                  <Flex.Item>
                    <Heading as="h2" level="h4">
                      <Text size="small">{t("programs.selectCourses")}</Text>
                    </Heading>
                  </Flex.Item>
                  <Flex.Item>
                    <Popover
                      isShowingContent={openContextPopover}
                      onShowContent={() => {
                        setOpenContextPopover(true);
                      }}
                      onHideContent={() => {
                        setOpenContextPopover(false);
                      }}
                      id={`add-context-popover`}
                      renderTrigger={
                        <CondensedButton
                          size="small"
                          interaction={
                            filteredCourses.length > 0 || noCourses
                              ? "enabled"
                              : "disabled"
                          }
                        >
                          {t("academicHierarchy.addExternalContext")}
                        </CondensedButton>
                      }
                      on="click"
                      screenReaderLabel={t(
                        "academicHierarchy.addExternalContext"
                      )}
                      mountNode={() => document.getElementById("main")}
                    >
                      <View padding="small" display="block" width={500}>
                        <AddContextPopover
                          setOpenSection={setOpenContextPopover}
                          setFilteredCourses={setFilteredCourses}
                          setSelectedCourses={setSelectedCourses}
                          availableDepartments={selectedDeps}
                        />
                      </View>
                    </Popover>
                  </Flex.Item>
                </Flex>

                <Card style={{ height: "350px", overflow: "auto" }}>
                  {!noCourses && filteredCourses.length === 0 ? (
                    <Alert variant="info">
                      {t("programs.selectDepartments")}
                    </Alert>
                  ) : noCourses ? (
                    <Alert variant="warning">
                      {t("programs.noCoursesFound")}
                    </Alert>
                  ) : (
                    <FormFieldGroup
                      description={
                        <ScreenReaderContent>
                          <span id="groupLabel">
                            {t("programs.selectCourses")}
                          </span>
                        </ScreenReaderContent>
                      }
                      rowSpacing="small"
                    >
                      {filteredCourses.map((course, key) => (
                        <View as="div" key={key}>
                          <Checkbox
                            size="small"
                            aria-labelledby={`groupLabel ${course.code}Label`}
                            label={
                              <span id={`${course.code}Label`}>
                                {course.code}
                              </span>
                            }
                            value={course.id}
                            onChange={(event) => {
                              updateSelectedCourses(event.target.value);
                            }}
                            checked={
                              selectedCourses.findIndex(
                                (c) => c.id === course.id
                              ) !== -1
                            }
                          />
                        </View>
                      ))}
                    </FormFieldGroup>
                  )}
                </Card>
              </View>
            </Flex.Item>
            <Flex.Item size="50%" padding="small">
              <View as="div">
                <Heading as="h2" level="h4" margin="0 0 small 0">
                  <Text size="small"> {t("programs.selectedCourses")}</Text>
                </Heading>
                <Card style={{ height: "350px", overflow: "auto" }}>
                  <List isUnstyled>
                    {selectedCourses.map((course, key) => (
                      <List.Item key={key} margin="0 0 small 0">
                        <Flex justifyItems="space-between">
                          <Flex.Item>
                            <Text size="small">{course.code}</Text>
                          </Flex.Item>
                          <Flex.Item>
                            <CondensedButton
                              data-testid={`remove-${course.code}`}
                              margin="0 0 xxx-small 0"
                              onClick={() => removeCourse(course.id)}
                            >
                              <ScreenReaderContent>
                                {t("global.remove") + " " + course.code}
                              </ScreenReaderContent>
                              <IconTrashLine />
                            </CondensedButton>
                          </Flex.Item>
                        </Flex>
                      </List.Item>
                    ))}
                  </List>
                </Card>
              </View>
            </Flex.Item>
          </Flex>
        </Modal.Body>
        <Modal.Footer>
          <Button margin="0 small" onClick={() => setOpen(false)}>
            {t("global.cancel")}
          </Button>
          <Button color="primary" onClick={handleSubmit} disabled={submitLoad}>
            {submitLoad ? (
              <Spinner
                renderTitle={t("programs.submittingCourseGroup")}
                size="x-small"
              />
            ) : (
              <Text>{t("global.save")}</Text>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      <ManageGroupTypesModal
        open={manageModalOpen}
        setOpen={setManageModalOpen}
        groupTypes={groupTypes}
        setGroupTypes={setGroupTypes}
      />
    </>
  );
};

export default CourseGroupModal;
